'use client'

import { Button } from '@/core/components/form/button/button'
import { useEffect } from 'react'
import {
  LuBell,
  LuGift,
  LuHeadphones,
  LuPenLine,
  LuReceipt,
} from 'react-icons/lu'

import { cn } from '@/core/utils/common'

import { SheetHead } from '@/features/veluvit/components/overlay/layer-parts'
import EmptyState from '@/features/veluvit/components/ui/empty-state'
import { ago } from '@/features/veluvit/lib/home'
import {
  loadNotifications,
  markNotificationRead,
  useNotificationStore,
} from '@/features/veluvit/services/notification.service'

import type { IconType } from 'react-icons'

/** 서버 알림 종류 → 아이콘. 모르는 종류는 종 */
const KIND_ICON: Record<string, IconType> = {
  reward: LuGift,
  payment: LuReceipt,
  refund: LuReceipt,
  creator: LuPenLine,
  support: LuHeadphones,
}

/** 알림함 시트 — 열 때마다 새로 받는다. 줄을 누르면 읽음. 안 읽은 줄은 제목이 밝고 오른쪽에 금색 점 */
export default function NotificationSheet() {
  const items = useNotificationStore((s) => s.items)
  const status = useNotificationStore((s) => s.status)

  useEffect(() => {
    void loadNotifications(true)
  }, [])

  return (
    <>
      <SheetHead title="알림" />
      {status === 'error' ? (
        <EmptyState
          compact
          icon={<LuBell />}
          title="알림을 불러오지 못했어요"
          action={
            <Button
              type="button"
              onClick={() => void loadNotifications()}
              className="cursor-pointer text-point underline underline-offset-4"
              variant="bare"
            >
              다시 불러오기
            </Button>
          }
        />
      ) : items.length ? (
        <ul className="-mx-2 grid gap-0.5">
          {items.map((item) => {
            const Icon = KIND_ICON[item.kind] ?? LuBell
            return (
              <li key={item.id}>
                <Button
                  type="button"
                  onClick={() => void markNotificationRead(item.id)}
                  className="flex w-full cursor-pointer items-start gap-3 rounded-field px-2 py-3 text-left transition-colors hover:bg-foreground/[0.05]"
                  variant="bare"
                >
                  <span
                    className={cn(
                      'grid size-9 flex-none place-items-center rounded-full',
                      item.read
                        ? 'bg-foreground/[0.06] text-t3'
                        : 'bg-point/15 text-point'
                    )}
                  >
                    <Icon aria-hidden="true" className="size-4.5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={cn(
                        'line-clamp-2 block text-c14 leading-[1.45] over10:text-c15',
                        item.read ? 'text-t3' : 'font-semibold text-t1'
                      )}
                    >
                      {item.title}
                    </span>
                    <span className="mt-0.5 block text-c12 text-t4 over10:text-c13">
                      {ago(item.createdAt)}
                    </span>
                  </span>
                  {!item.read && (
                    <span
                      aria-label="안 읽음"
                      className="mt-2 size-2 flex-none rounded-full bg-sub"
                    />
                  )}
                </Button>
              </li>
            )
          })}
        </ul>
      ) : status === 'ready' ? (
        <EmptyState
          compact
          icon={<LuBell />}
          title="새 알림이 없어요"
          description="보상, 결제, 작품 심사와 문의 답변 소식이 여기로 와요."
        />
      ) : (
        <p className="py-10 text-center text-c14 text-t3 over10:text-c15">
          불러오는 중…
        </p>
      )}
    </>
  )
}
